import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Mail, Phone, Trash2 } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const statusColors: Record<string, string> = {
  completed: "bg-accent/10 text-accent border-accent/20",
  no_show: "bg-destructive/10 text-destructive border-destructive/20",
  confirmed: "bg-primary/10 text-primary border-primary/20",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  cancelled: "bg-muted text-muted-foreground border-border",
  awaiting_payment: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  expired: "bg-muted text-muted-foreground border-border",
};

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [customer, setCustomer] = useState<any>(null);
  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const { data: c } = await supabase.from("customers").select("*").eq("id", id).maybeSingle();
      setCustomer(c);
      if (c) {
        const { data: r } = await supabase
          .from("reservations")
          .select("*, service:services(name)")
          .eq("customer_id", c.id)
          .order("date", { ascending: false });
        setReservations(r || []);
      }
      setLoading(false);
    };
    fetch();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 text-muted-foreground">
          <Link to="/dashboard/customers"><ArrowLeft className="mr-2 h-4 w-4" />{t("customers.back")}</Link>
        </Button>
        <p className="text-muted-foreground">{t("customers.not_found")}</p>
      </div>
    );
  }

  const initials = customer.name.split(" ").map((n: string) => n[0]).join("");
  const totalSpent = reservations.reduce((sum: number, r: any) => sum + Number(r.price), 0);

  return (
    <div className="p-6">
      <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 text-muted-foreground">
        <Link to="/dashboard/customers"><ArrowLeft className="mr-2 h-4 w-4" />{t("customers.back")}</Link>
      </Button>

      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary shrink-0">{initials}</div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-foreground">{customer.name}</h1>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5 break-all"><Mail className="h-3.5 w-3.5 shrink-0" />{customer.email}</span>
                {customer.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 shrink-0" />{customer.phone}</span>}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive shrink-0 w-full sm:w-auto" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="h-4 w-4 mr-1" />{t("customers.delete")}
          </Button>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-4 mb-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4 shadow-card">
          <p className="text-xs text-muted-foreground mb-1">{t("customers.total_bookings")}</p>
          <p className="text-2xl font-bold text-foreground">{reservations.length}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow-card">
          <p className="text-xs text-muted-foreground mb-1">{t("customers.total_spent")}</p>
          <p className="text-2xl font-bold text-foreground">€{totalSpent.toFixed(2)}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow-card">
          <p className="text-xs text-muted-foreground mb-1">{t("customers.customer_since")}</p>
          <p className="text-sm font-semibold text-foreground">{format(new Date(customer.created_at), "MMM d, yyyy")}</p>
        </div>
      </div>

      <section className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">{t("customers.reservation_history")}</h2>
        </div>
        {reservations.length > 0 ? (
          <>
            <table className="w-full hidden md:table">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("customers.col_service")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("customers.col_date")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("customers.col_price")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("customers.col_status")}</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r: any) => (
                  <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{r.service?.name || "—"}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{format(new Date(r.date), "MMM d, yyyy · HH:mm")}</td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">€{Number(r.price).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize ${statusColors[r.status] || ""}`}>
                        {r.status.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="md:hidden divide-y divide-border">
              {reservations.map((r: any) => (
                <div key={r.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground">{r.service?.name || "—"}</p>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize ${statusColors[r.status] || ""}`}>
                      {r.status.replace("_", " ")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                    <span>{format(new Date(r.date), "MMM d, yyyy · HH:mm")}</span>
                    <span className="font-medium text-foreground">€{Number(r.price).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">{t("customers.no_reservations")}</div>
        )}
      </section>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("customers.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("customers.delete_desc_pre")} <span className="font-medium text-foreground">{customer.name}</span>{t("customers.delete_desc_post")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("customers.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const { error } = await supabase.from("customers").delete().eq("id", customer.id);
                if (error) {
                  toast.error(error.message);
                } else {
                  toast.success(`${customer.name} ${t("customers.deleted")}`);
                  navigate("/dashboard/customers");
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("customers.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerDetailPage;